import { useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import "./DocsLandingPage.scss";

const DocsLandingPage = () => {
  const navigate = useNavigate();

  const categories = [
    {
      id: "getting-started",
      icon: "bi-play-circle",
      color: "success",
      title: "Getting Started",
      description: "Learn the basics and set up your account",
      features: [
        "Creating your first bookmark",
        "Setting up collections",
        "Account configuration",
      ],
      link: "/docs/getting-started",
    },
    {
      id: "features",
      icon: "bi-stars",
      color: "primary",
      title: "Core Features",
      description: "Master bookmarks, collections, and search",
      features: [
        "Managing bookmarks",
        "Organizing collections",
        "Workspace management",
      ],
      link: "/docs/bookmarks",
    },
    {
      id: "advanced",
      icon: "bi-shield-lock",
      color: "info",
      title: "Advanced Features",
      description: "Security, encryption, and spaces",
      features: ["Client-side encryption", "Privacy settings", "Data security"],
      link: "/docs/encryption",
    },
    {
      id: "account",
      icon: "bi-person-gear",
      color: "warning",
      title: "Account & Settings",
      description: "Manage your account and subscription",
      features: [
        "Account settings",
        "Subscription plans",
        "Billing management",
      ],
      link: "/docs/settings",
    },
  ];

  const faqs = [
    {
      question: "How do I import bookmarks from my browser?",
      answer: `You can easily import bookmarks from any browser:
        1. Go to Settings → Import/Export
        2. Choose "Import from Browser"
        3. Select your browser and follow the prompts
        4. Our system will automatically organize imported bookmarks
        
        Supported browsers: Chrome, Firefox, Safari, Edge, Opera`,
      relatedDoc: "/docs/bookmarks",
    },
    {
      question: "What makes onBookmarks different from browser bookmarks?",
      answer: `onBookmarks offers several advantages:
        • Cross-browser access: Your bookmarks work on any browser
        • Smart organization: Collections and spaces for better structure
        • Advanced search: Find bookmarks by content, not just title
        • Encryption: Keep sensitive bookmarks private
        • Multi-device sync: Access your bookmarks anywhere`,
      relatedDoc: "/docs/getting-started",
    },
    {
      question: "How does client-side encryption work?",
      answer: `Our encryption ensures that only you can read your private bookmarks:
        • Encryption happens on your device before data reaches our servers
        • We use industry-standard encryption protocols
        • Your encryption keys never leave your device
        • Even we cannot decrypt your private bookmarks
        
        Important: If you lose your encryption key, we cannot recover your encrypted bookmarks.`,
      relatedDoc: "/docs/encryption",
    },
    {
      question: "Can I organize bookmarks into multiple collections?",
      answer: `Yes! Our flexible organization system allows:
        • Create unlimited collections to organize your bookmarks
        • Add one bookmark to multiple collections
        • Create spaces for different contexts (work, personal, projects)
        • Smart collections that auto-organize based on rules`,
      relatedDoc: "/docs/collections",
    },
    {
      question: "Is there a limit to how many bookmarks I can save?",
      answer: `All plans include unlimited bookmarks and collections and full search capabilities.`,
      relatedDoc: "/docs/subscription",
    },
  ];

  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="docs-landing-page">
      {/* Hero Section */}
      <section className="bg-primary-gradient text-white py-5 position-relative overflow-hidden">
        <Container>
          <Row>
            <Col lg={10} className="mx-auto text-center">
              <div className="d-inline-flex align-items-center bg-white bg-opacity-25 rounded fw-semibold px-3 py-2 mb-4">
                <i className="bi bi-book me-2 fs-4"></i>
                <span>Help Center</span>
              </div>
              <h1 className="display-4 fw-bold mb-4 text-white">
                Documentation & Support
              </h1>
              <p className="lead mb-5 text-white">
                Everything you need to get the most out of onBookmarks
              </p>
            </Col>
          </Row>
        </Container>

        {/* Floating Elements */}
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ pointerEvents: "none" }}
        >
          <i
            className="bi bi-question-circle position-absolute opacity-25 fs-1 text-white floating-animation"
            style={{ top: "20%", left: "10%" }}
          ></i>
          <i
            className="bi bi-lightbulb position-absolute opacity-25 fs-1 text-white floating-animation"
            style={{ top: "60%", right: "15%", animationDelay: "2s" }}
          ></i>
        </div>
      </section>

      {/* Quick Help Categories */}
      <section className="py-5">
        <Container>
          <Row className="g-4">
            {categories.map((category) => (
              <Col key={category.id} lg={3} md={6}>
                <Card
                  className="h-100 border shadow-sm category-card"
                  onClick={() => navigate(category.link)}
                  role="button"
                  style={{ cursor: "pointer", transition: "all 0.3s ease" }}
                >
                  <Card.Body className="p-4">
                    <div
                      className={`icon icon-lg mb-3 mx-auto bg-${category.color} bg-opacity-10`}
                      style={{ width: "64px", height: "64px" }}
                    >
                      <i
                        className={`bi ${category.icon} fs-2 text-${category.color}`}
                      ></i>
                    </div>
                    <h5 className="fw-bold text-center mb-3">
                      {category.title}
                    </h5>

                    <ul className="list-unstyled">
                      {category.features.map((feature, index) => (
                        <li key={index} className="mb-2 position-relative ps-4">
                          <span className="position-absolute start-0 text-success fw-bold">
                            ✓
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="pb-5 bg-white">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <h2 className="text-center fw-bold mb-5">
                Frequently Asked Questions
              </h2>

              <div>
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border rounded-3 mb-3"
                    onMouseEnter={(e) =>
                      e.currentTarget.classList.add("bg-light")
                    }
                    onMouseLeave={(e) =>
                      e.currentTarget.classList.remove("bg-light")
                    }
                  >
                    <button
                      className="btn btn-link text-start w-100 text-decoration-none p-3 text-primary fw-semibold position-relative pe-5"
                      onClick={() => toggleFaq(index)}
                    >
                      {faq.question}
                      <span
                        className="position-absolute end-0 top-50 translate-middle-y me-3 fs-5 fw-bold"
                        style={{
                          transform:
                            activeFaq === index
                              ? "translateY(-50%) rotate(45deg)"
                              : "translateY(-50%)",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        +
                      </span>
                    </button>
                    <div
                      className={`px-3 pb-3 ${
                        activeFaq === index ? "d-block" : "d-none"
                      }`}
                    >
                      <div className="text-muted">
                        {faq.answer.split("\n").map((line, i) => (
                          <p key={i} className="mb-2">
                            {line}
                          </p>
                        ))}
                      </div>
                      {faq.relatedDoc && (
                        <Link
                          to={faq.relatedDoc}
                          className="btn btn-sm btn-primary mt-3"
                        >
                          Learn more →
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-5">
                <p className="text-muted mb-3">
                  Can't find what you're looking for?
                </p>
                <Link
                  to="/documentation/getting-started"
                  className="btn btn-primary"
                >
                  Browse all documentation
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default DocsLandingPage;
